import { observer } from 'mobx-react';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, DialogProps, Stack, Typography } from '@mui/material';
import Avatar from 'components/Avatar';
import Modal from 'components/Modal';
import store from 'store';

import { IStudentModel } from '@common/models/Users/Students/Student';

import { ModalContent, StudentCard, StudentInfo, StudentName, StudentSubtitle } from './styled';

const STORAGE_PREFIX = 'student-picker-shown-';
type StudentPickerModalProps = Partial<Omit<DialogProps, 'onClose' | 'open' | 'children'>>;

const isParent = () => store.profile.roles?.some((role) => role.name === 'Parent');

const getSubtitle = (student: IStudentModel) =>
	student?.groups?.[0]?.official_name || student?.groups?.[0]?.name || '';

const isSessionSeen = (key: string) =>
	typeof window !== 'undefined' ? !!sessionStorage.getItem(key) : false;

const markSessionSeen = (key: string) => {
	if (typeof window !== 'undefined') {
		sessionStorage.setItem(key, 'true');
	}
};

function StudentPickerModal(props: StudentPickerModalProps) {
	const { t } = useTranslation();
	const [open, setOpen] = useState(false);
	const [selectedId, setSelectedId] = useState<number | null>(store.currentStudentId);

	const students = store.profile.parent?.students || ([] as IStudentModel[]);
	const tokenId = store.auth.user?.jti || store.auth.user?.sub;

	const hasMultipleStudents = students.length > 1;
	const storageKey = useMemo(() => (tokenId ? `${STORAGE_PREFIX}${tokenId}` : null), [tokenId]);

	useEffect(() => {
		setSelectedId(store.currentStudentId);
	}, [store.currentStudentId]);

	useEffect(() => {
		if (!isParent() || !hasMultipleStudents || !storageKey) {
			setOpen(false);
			return;
		}

		const isSeen = isSessionSeen(storageKey);
		if (!isSeen) {
			setOpen(true);
		}
	}, [hasMultipleStudents, storageKey]);

	const markSeen = () => {
		if (storageKey) {
			markSessionSeen(storageKey);
		}
	};

	const handleClose = () => {
		setOpen(false);
		markSeen();
	};

	const handleSelect = (student: IStudentModel) => {
		setSelectedId(student.id);
		const serviceId = student.services_ids?.[0];
		if (serviceId) {
			store.services.select(serviceId);
		}
		store.selectStudent(student.id);
		markSeen();
		setOpen(false);
	};

	if (!isParent() || !hasMultipleStudents) {
		return null;
	}

	return (
		<Modal
			open={open}
			onClose={handleClose}
			fullWidth
			maxWidth="sm"
			title={t('auth.selectChildAccount')}
			{...props}
		>
			<ModalContent>
				<Stack spacing={2}>
					{students.map((student: IStudentModel) => (
						<StudentCard
							key={student.id}
							onClick={() => handleSelect(student)}
							data-selected={student.id === selectedId}
						>
							<Box display="flex" alignItems="center" gap={1.5} flex={1}>
								{student.id === selectedId ? (
									<CheckCircleOutlineIcon color="primary" />
								) : (
									<RadioButtonUncheckedIcon color="action" />
								)}
								<Avatar
									src={student.avatar_url || student.avatar?.url}
									alt={`${student.first_name} ${student.last_name}`}
									size={56}
								/>
								<StudentInfo>
									<StudentName>{[student.first_name, student.last_name].join(' ')}</StudentName>
									{getSubtitle(student) && (
										<StudentSubtitle>{getSubtitle(student)}</StudentSubtitle>
									)}
								</StudentInfo>
							</Box>
							<Typography variant="body2" color="text.secondary">
								{/* keeps layout aligned with icons */}
							</Typography>
						</StudentCard>
					))}
				</Stack>
			</ModalContent>
		</Modal>
	);
}

export default observer(StudentPickerModal);
